import { Link } from 'react-router-dom'
import { Icon } from '../../components/icons/Icon'
import type { ColumnDef } from '../../design-system/list-view/types'
import type { ContactView } from '../../types/contact'

/**
 * Column order per IB-48 design §4.2 (name / account / email / title).
 * `Contact` gained a detail page in the CRM-completeness pass (IB-93) — the
 * `name` cell is now a `Link` to `/clients/contacts/:id`, mirroring
 * Accounts'/Legal entities' `name` columns; the whole row also activates
 * that same navigation (`onRowActivate`, see `ContactsListScreen`), with
 * "Edit" available as a distinct row action for the fast in-place path.
 *
 * `accountName` mirrors the `accountName` column in
 * `screens/cases/caseColumns.tsx`: `sortable: false` because the backend's
 * `RsqlToSpecification` allow-list for Contact (design §2.3) only sorts
 * `{name, email, createdAt, id}` — the enriched parent name is not a
 * persisted column. Filtering by account is still available via the
 * `AccountLookupField` facet in `ContactsListScreen` (filters by id).
 *
 * `email`/`title` are Class: PII (design §2.3, §2.6) — rendered here for the
 * legitimate operator use case of contacting this person, never logged.
 *
 * IB-27 S3 retrofit target: `title` is inline-editable (design §4c) when `onEditTitle` is
 * supplied — a plain job-title label, non-guarded, non-identity. Deliberately NOT `name`/`email`
 * even though both are patchable: keeping PII out of inline-edit grid chrome is a defensible safe
 * pick, not the maximal one (design §4c). Callers that omit `onEditTitle` (e.g.
 * `AccountDetailScreen`'s related-contacts list) get the exact same read-only column as before —
 * zero behavior change (AC11).
 */
export interface ContactColumnOptions {
  /** Wired by `ContactsListScreen` to `patchContact(id, { title }, version)`. Rejects with the
   * server's own `Error`/`ApiError` — `DataGrid` shows its `.message` inline verbatim (AC5). */
  onEditTitle?: (row: ContactView, next: string) => Promise<void>
}

export function buildContactColumns(options: ContactColumnOptions = {}): ColumnDef<ContactView>[] {
  return [
    {
      id: 'name',
      header: 'Contact',
      width: 220,
      minWidth: 160,
      sortable: true,
      pinnable: true,
      toExportText: (row) => row.name,
      render: (row) => (
        <Link
          to={`/clients/contacts/${row.id}`}
          style={{ display: 'inline-flex', alignItems: 'center', gap: 6, color: 'var(--iw-accent)', textDecoration: 'none' }}
        >
          <Icon name="users" size={14} />
          {row.name}
        </Link>
      ),
    },
    {
      id: 'accountName',
      header: 'Account',
      width: 220,
      sortable: false,
      toExportText: (row) => row.accountName ?? '',
      render: (row) => row.accountName ?? <span style={{ color: 'var(--iw-text-subtle)' }}>—</span>,
    },
    {
      id: 'email',
      header: 'Email',
      width: 220,
      sortable: true,
      toExportText: (row) => row.email,
      render: (row) => row.email,
    },
    {
      id: 'title',
      header: 'Title',
      width: 180,
      sortable: false,
      toExportText: (row) => row.title ?? '',
      render: (row) => row.title ?? <span style={{ color: 'var(--iw-text-subtle)' }}>—</span>,
      editable: options.onEditTitle
        ? {
            getValue: (row) => row.title ?? '',
            commit: (row, next) => options.onEditTitle!(row, next),
          }
        : undefined,
    },
  ]
}

export const CONTACT_COLUMN_ORDER = ['name', 'accountName', 'email', 'title']
